import { takeLatest, put, call, select } from 'redux-saga/effects';
import type { AxiosResponse, AxiosError } from 'axios';

import { ROLE_ACCESS_STORE_CONST } from '../actionConstants';
import { APPLICATION_CONSTANTS, PATHS } from 'AppConstants';
import { ROLE_ACCESS_CONSTANTS, ROLE_ACCESS_NOTIFICATION_MESSAGES, VIEW_ASSIGNED_USERS_INITIAL_PAGE, VIEW_ASSIGNED_USERS_PAGE_LIMIT } from '../../RoleAccess/config';

import { createOrUpdateRoleApi, getAllRolesApi, getRolesAggregatedApi, deleteRoleApi, getEmployeeActiveRolesApi, getRoleEmployeesApi } from '../../apis/rolesApi';

import {
	fetchRolesAggregated,
	setAllRoles,
	setRoleModal,
	setRoleModalData,
	setRoleApiError,
	setRolesAggregated,
	setRolesAggregatedError,
	setEmployeeActiveRoles,
	setEmployeeActiveRolesError,
	setRoleEmployees,
	setRoleEmployeesError
} from '../actions/roleAccessActions';
import { setApiLoading, setApiError, setApiSuccess } from '../actions/employeeActions';

import { saveServerLogs } from 'UtilityComponents/Util';

import type {
	CreateRoleApiResponse,
	GetAllRolesApiResponse,
	GetRolesAggregatedApiResponse,
	GetEmployeeActiveRolesRequest,
	GetEmployeeActiveRolesApiResponse,
	GetRoleEmployeesApiResponse,
	GetRoleEmployeesRequest,
	RoleEmployeesListState,
	ApiErrorResponse,
	RolesAggregatePayload,
	RoleMutationPayload,
	DeleteRolePayload
} from '../../RoleAccess/types';

interface RoleMutationAction {
	type: string;
	payload: RoleMutationPayload;
}

interface DeleteRoleAction {
	type: string;
	payload: DeleteRolePayload;
}

type RoleNotifyApiType = keyof typeof ROLE_ACCESS_NOTIFICATION_MESSAGES;

const getRoleNotifyMessage = (apiType: RoleNotifyApiType, status: 'success' | 'error'): string => {
	const messages = ROLE_ACCESS_NOTIFICATION_MESSAGES[apiType];
	if (status === 'success' && 'success' in messages) {
		return messages.success;
	}
	return messages.error;
};

/**
 * @function createRoleSaga
 * @description Saga to handle role creation
 * 
 * @param {RoleAction} action - The action containing role data
 */
function* createRoleSaga({ payload }: RoleMutationAction): Generator<unknown, void, AxiosResponse<CreateRoleApiResponse>> {
	const { roleData, refetchAfterSuccess } = payload;

	try {
		yield put(setApiLoading(true, true, false, ROLE_ACCESS_STORE_CONST.CREATE_ROLE));

		const response: AxiosResponse<CreateRoleApiResponse> = yield call(createOrUpdateRoleApi, roleData);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			yield put(setRoleModalData({ roleName: data.role, refetchAfterSuccess }));
			yield put(setRoleModal(ROLE_ACCESS_CONSTANTS.ROLE_SUCCESS_MODAL));
			yield put(setApiLoading(false, false, false, ''));
			if (refetchAfterSuccess) {
				yield put(fetchRolesAggregated());
			}
		} else {
			throw new Error(getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.CREATE_ROLE, 'error'));
		}
	} catch (error) {
		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.CREATE_UPDATE_ROLE);
		}
		yield put(setRoleModal(false));
		yield put(setApiError({
			data: {
				msg: getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.CREATE_ROLE, 'error')
			},
			apiType: ROLE_ACCESS_STORE_CONST.CREATE_ROLE,
			showNotify: true
		}));
	}
}

/**
 * @function updateRoleSaga
 * @description Saga to handle role update
 * 
 * @param {RoleAction} action - The action containing role data
 */
function* updateRoleSaga({ payload }: RoleMutationAction): Generator<unknown, void, AxiosResponse<CreateRoleApiResponse>> {
	const { roleData, refetchAfterSuccess } = payload;

	try {
		yield put(setApiLoading(true, true, false, ROLE_ACCESS_STORE_CONST.UPDATE_ROLE));

		const response: AxiosResponse<CreateRoleApiResponse> = yield call(createOrUpdateRoleApi, roleData);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			yield put(setRoleModal(false));
			yield put(setApiSuccess({
				data: {
					msg: getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.UPDATE_ROLE, 'success')
				},
				apiType: ROLE_ACCESS_STORE_CONST.UPDATE_ROLE,
				showNotify: true
			}));
			if (refetchAfterSuccess) {
				yield put(fetchRolesAggregated());
			}
		} else {
			throw new Error(getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.UPDATE_ROLE, 'error'));
		}
	} catch (error) {
		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.CREATE_UPDATE_ROLE);
		}
		yield put(setRoleModal(false));
		yield put(setApiError({
			data: {
				msg: getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.UPDATE_ROLE, 'error')
			},
			apiType: ROLE_ACCESS_STORE_CONST.UPDATE_ROLE,
			showNotify: true
		}));
	}
}

/**
 * @function fetchAllRolesSaga
 * @description Saga to handle fetching all roles
 */
function* fetchAllRolesSaga(): Generator<unknown, void, AxiosResponse<GetAllRolesApiResponse>> {
	try {
		const response: AxiosResponse<GetAllRolesApiResponse> = yield call(getAllRolesApi);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			yield put(setAllRoles(data.activeRoles || []));
		} else {
			const errorMsg = data?.message || 'Failed to fetch roles';
			throw new Error(errorMsg);
		}
	} catch (error) {
		const axiosError = error as AxiosError<ApiErrorResponse>;
		const errorMsg = axiosError.response?.data?.message || (error as Error).message || 'Something went wrong';

		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.GET_ALL_ROLES);
		}
		yield put(setRoleApiError());
	}
}

/**
 * @function fetchRolesAggregatedSaga
 * @description Saga to handle fetching active roles together with aggregated counts
 * (used by the Role & Access page).
 */
function* fetchRolesAggregatedSaga(): Generator<unknown, void, AxiosResponse<GetRolesAggregatedApiResponse>> {
	try {
		const response: AxiosResponse<GetRolesAggregatedApiResponse> = yield call(getRolesAggregatedApi);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			const aggregatePayload: RolesAggregatePayload = {
				activeRoles: data.activeRoles || [],
				counts: {
					rolesCount: data.rolesCount || 0,
					assignedUsers: data.assignedUsers || 0,
					activePermissions: data.activePermissions || 0
				}
			};
			yield put(setRolesAggregated(aggregatePayload));
		} else {
			const errorMsg = data?.message || 'Failed to fetch aggregated roles';
			throw new Error(errorMsg);
		}
	} catch (error) {
		const axiosError = error as AxiosError<ApiErrorResponse>;
		const errorMsg = axiosError.response?.data?.message || (error as Error).message || 'Something went wrong';

		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.GET_ROLES_AGGREGATED);
		}
		yield put(setRolesAggregatedError());
	}
}

/**
 * @function deleteRoleSaga
 * @description Saga to handle role deletion
 */
function* deleteRoleSaga({ payload }: DeleteRoleAction): Generator<unknown, void, AxiosResponse<CreateRoleApiResponse>> {
	const { roleId, refetchAfterSuccess } = payload;

	try {
		yield put(setApiLoading(true, true, false, ROLE_ACCESS_STORE_CONST.DELETE_ROLE));

		const response: AxiosResponse<CreateRoleApiResponse> = yield call(deleteRoleApi, roleId);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			yield put(setRoleModal(false));
			yield put(setApiSuccess({
				data: {
					msg: getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.DELETE_ROLE, 'success')
				},
				apiType: ROLE_ACCESS_STORE_CONST.DELETE_ROLE,
				showNotify: true
			}));
			if (refetchAfterSuccess) {
				yield put(fetchRolesAggregated());
			}
		} else {
			throw new Error(getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.DELETE_ROLE, 'error'));
		}
	} catch (error) {
		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.GET_ALL_ROLES);
		}

		yield put(setRoleModal(false));
		yield put(setApiError({
			data: {
				msg: getRoleNotifyMessage(ROLE_ACCESS_STORE_CONST.DELETE_ROLE, 'error')
			},
			apiType: ROLE_ACCESS_STORE_CONST.DELETE_ROLE,
			showNotify: true
		}));
	}
}

/**
 * @function fetchEmployeeActiveRolesSaga
 * @description Saga to fetch currently assigned roles for the supplied employees
 *
 * @param {{ type: string; payload: GetEmployeeActiveRolesRequest }} action - Action containing the list of employeeEmailCommIds
 */
function* fetchEmployeeActiveRolesSaga({ payload }: { type: string; payload: GetEmployeeActiveRolesRequest }): Generator<unknown, void, AxiosResponse<GetEmployeeActiveRolesApiResponse>> {
	try {
		const response: AxiosResponse<GetEmployeeActiveRolesApiResponse> = yield call(getEmployeeActiveRolesApi, payload);
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			yield put(setEmployeeActiveRoles(data.employeeActiveRoles || []));
		} else {
			const errorMsg = data?.message || 'Failed to fetch employee roles';
			throw new Error(errorMsg);
		}
	} catch (error) {
		const axiosError = error as AxiosError<ApiErrorResponse>;
		const errorMsg = axiosError.response?.data?.message || (error as Error).message || 'Something went wrong';

		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.GET_EMPLOYEE_ROLES);
		}

		yield put(setEmployeeActiveRolesError());
	}
}

/**
 * @function fetchRoleEmployeesSaga
 * @description Saga to fetch employees assigned to a role
 */
function* fetchRoleEmployeesSaga({ payload }: { type: string; payload: GetRoleEmployeesRequest }): Generator<unknown, void, unknown> {
	const { roleId, page = VIEW_ASSIGNED_USERS_INITIAL_PAGE, limit = VIEW_ASSIGNED_USERS_PAGE_LIMIT } = payload;

	try {
		const response = (yield call(getRoleEmployeesApi, roleId, { page, limit })) as AxiosResponse<GetRoleEmployeesApiResponse>;
		const { data } = response;

		if (data && data.responseCode === APPLICATION_CONSTANTS.SUCCESS_RESPONSE_CODE) {
			const pageEmployees = data.employees || [];
			const hasMore = pageEmployees.length > 0 && pageEmployees.length >= limit;

			const roleEmployeesPayload: RoleEmployeesListState = {
				role: data.role,
				roleDescription: data.roleDescription,
				usersTagged: data.usersTagged,
				employees: pageEmployees,
				hasMore,
				loading: false,
				error: false
			};
			yield put(setRoleEmployees(roleEmployeesPayload));
		} else {
			const errorMsg = data?.message || 'Failed to fetch assigned users';
			throw new Error(errorMsg);
		}
	} catch (error) {
		if ((error as Error).stack) {
			saveServerLogs((error as Error).stack, 'error', PATHS.GET_ROLE_EMPLOYEES);
		}

		yield put(setRoleEmployeesError());
	}
}

export default [
	takeLatest(ROLE_ACCESS_STORE_CONST.CREATE_ROLE, createRoleSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.UPDATE_ROLE, updateRoleSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.DELETE_ROLE, deleteRoleSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.FETCH_ALL_ROLES, fetchAllRolesSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.FETCH_ROLES_AGGREGATED, fetchRolesAggregatedSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.FETCH_EMPLOYEE_ACTIVE_ROLES, fetchEmployeeActiveRolesSaga),
	takeLatest(ROLE_ACCESS_STORE_CONST.FETCH_ROLE_EMPLOYEES, fetchRoleEmployeesSaga)
];
